import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Building2, Globe, Target, ShieldCheck, Trophy } from 'lucide-react';

export default function AboutPage() {
  const stats = [
    { value: '10,000+', label: 'Businesses Served', icon: Building2 },
    { value: '5,000+', label: 'Verified CAs', icon: Users },
    { value: '500+', label: 'Cities Covered', icon: Globe },
    { value: '99.4%', label: 'On-time Delivery', icon: ShieldCheck },
  ];

  const values = [
    { title: 'Transparency', desc: 'Fixed pricing, zero hidden charges, complete visibility into every step of your engagement.' },
    { title: 'Expertise', desc: 'Every professional on our platform is a qualified CA, CS or tax expert, verified for credentials and experience.' },
    { title: 'Speed', desc: 'Faster turnarounds than traditional CA firms — backed by clear service-level guarantees.' },
    { title: 'Trust', desc: 'Your data, contracts and communications are encrypted and confidential. NDAs available on request.' },
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-[#f4f9fd] via-white to-[#fff4ec] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-orange-600">About Register Point</div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0b3d5b] mt-3">Making tax &amp; compliance simple for every Indian business</h1>
          <p className="mt-4 text-slate-600 max-w-3xl mx-auto leading-relaxed">Register Point is India&apos;s trusted online marketplace connecting businesses with verified Chartered Accountants, Company Secretaries and tax experts across 500+ cities. Since inception, we&apos;ve helped 10,000+ businesses handle every compliance from GST to FDI.</p>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-orange-100 mx-auto flex items-center justify-center">
                  <Icon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="mt-3 text-3xl font-extrabold text-[#0b3d5b]">{s.value}</div>
                <div className="text-sm text-slate-600">{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50/60 py-14">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-orange-600">Our Story</div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0b3d5b] mt-2">A decade of helping businesses grow</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Register Point began with a simple idea — the best CAs shouldn&apos;t be limited by geography. What started as a small consulting practice in Noida grew into a nationwide marketplace serving businesses in every state.</p>
            <p className="mt-3 text-slate-600 leading-relaxed">Today, we combine deep tax expertise with modern technology to deliver fast, transparent and reliable service to entrepreneurs, SMEs and enterprises alike.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-xs text-slate-700"><Award className="w-4 h-4 text-orange-500" /> Best Digital Financial Service 2017</span>
              <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-xs text-slate-700"><Trophy className="w-4 h-4 text-orange-500" /> Entrepreneur Award 2020</span>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl border border-orange-100 p-8">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">KM</div>
              <div>
                <div className="font-bold text-[#0b3d5b] text-lg"></div>
                <div className="text-sm text-slate-500">Founder &amp; CEO</div>
              </div>
            </div>
            <p className="mt-5 text-slate-600 leading-relaxed italic">&ldquo;Every business deserves world-class financial advice, regardless of size or location. That&apos;s the mission that drives us every day at Register Point.&rdquo;</p>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-orange-600">What We Stand For</div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0b3d5b] mt-2">Our Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="mt-4 font-bold text-[#0b3d5b]">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-[#0b3d5b] to-[#0d4a6f] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-extrabold">Ready to make compliance simple?</h2>
          <p className="mt-3 text-white/85">Talk to a verified CA on Register Point today and see the difference.</p>
          <Link to="/contact" className="mt-6 inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors">Get in touch</Link>
        </div>
      </section>
    </div>
  );
}
